/**
 * 진입점이자 화면 전환기.
 *
 *   출정 준비 → 전투 → 결과 → (더 들어가기 | 철수) → 출정 준비
 *
 * 출정 준비에서 장비 화면으로 빠질 수 있다. 전투 중에는 갈 수 없다.
 *
 * 계정 병력은 브라우저에 저장된다. 병사는 죽으면 저장에서도 사라진다.
 */
import { NIGHT_SIGHT, NIGHT_SIGHT_TORCH, Renderer } from './render/renderer.ts';
import { Hud, mateSkillOf } from './render/hud.ts';
import { squadName } from './ui/labels.ts';
import { InputState } from './game/input.ts';
import { GameLoop, HOLD_LEAVE } from './game/loop.ts';
import { installLeaveGuard } from './ui/leave-guard.ts';
import { ReplayLoop, ReplayPlayer } from './game/replay-player.ts';
import { MomentTracker, momentTone } from './game/replay-moments.ts';
import { addRecord, clearRecords, loadRecords, saveAccountThenRecords } from './meta/replays.ts';
import type { ReplayRecord } from './meta/replays.ts';
import { renderRecords } from './ui/records.ts';
import { TERRITORY_BY_ID } from './meta/realm.ts';
import { ReplayView } from './ui/replay-view.ts';
import type { ReplayViewState } from './ui/replay-view.ts';
import { REPLAY_VERSION, runReplay } from './sim/replay.ts';
import type { Replay } from './sim/replay.ts';
import { battleSupplies, battleTraits, createBattle } from './sim/scenario.ts';
import { Supply } from './sim/supplies.ts';
import { hashToHex } from './sim/checksum.ts';
import { Team } from './sim/units.ts';
import {
  clearArmy,
  createStartingArmy,
  loadArmy,
  nextRunSeed,
  saveArmy,
  payWage,
  realmOf,
  soldierById,
  canRaid,
} from './meta/army.ts';
import { autoPlan, restorePlan } from './meta/plan.ts';
import type { Army } from './meta/army.ts';
import {
  advance,
  applyBattle,
  buildConfig,
  chooseRoute,
  chooseSupplies,
  needsPrep,
  chooseDeploy,
  needsRoute,
  backFromCrossroads,
  pickSquads,
  retreat,
  retreatReceipt,
  startRun,
  startTerritoryRun,
} from './meta/run.ts';
import { renderBattlePrep } from './ui/battle-prep.ts';
import { renderCrossroads } from './ui/crossroads.ts';
import { Trait, gateStoryName } from './sim/traits.ts';
import type { BattleOutcome, RunState, Selection } from './meta/run.ts';
import type { ScenarioConfig } from './sim/scenario.ts';
import { currentPlaytest, isPlaytest, playtestLabel } from './game/playtest.ts';
import { SortieScreen } from './ui/sortie.ts';
import { ArmoryScreen } from './ui/armory.ts';
import { StatsScreen } from './ui/stats.ts';
import { renderResult } from './ui/result.ts';
import { homecomingBar } from './ui/homecoming.ts';
import type { Homecoming } from './ui/homecoming.ts';
import { button, clear, el } from './ui/dom.ts';
import { BattleControls } from './ui/battle-controls.ts';
import { renderJoinedScene } from './ui/joined.ts';
import { applyTheme, savedTheme, watchSystemTheme } from './ui/theme.ts';
import { BattleSound, initSound } from './audio/sound.ts';
import { companionToGreet } from './ui/companion-lore.ts';
import { closeItemTip } from './ui/items.ts';
import { fanfaresFor, growthSnapshot, showFanfares } from './ui/fanfare.ts';
import type { GrowthSnapshot } from './ui/fanfare.ts';
import { Order } from './sim/commands.ts';
import { COMPANIONS, Culture, RAID_BANDS, foeHeadSquad, raidBand } from './sim/troops.ts';
import { fallTitle, fallenLeaders, foeLeaderOf } from './meta/foe-leaders.ts';
import { renderCulturePick } from './ui/culture-pick.ts';
import { RealmScreen, newsLine } from './ui/realm.ts';
import { isOurNews, markNewsSeen } from './meta/realm.ts';


interface BattleSession {
  renderer: Renderer;
  loop: GameLoop;
  hud: Hud;
  input: InputState;
  controls: BattleControls;
  world: ReturnType<typeof createBattle>;
  /** 이 전투를 만든 명부. 정산도 이것으로 한다. 서버가 가진 것과 같아야 한다. */
  config: ScenarioConfig;
  ended: boolean;
  /** 이 판의 주요 장면. 기록에 싣는다. */
  moments?: MomentTracker;
  /** 이 판의 소리(`audio/sound.ts`). */
  sound?: BattleSound;
}

function need(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`화면 요소를 찾을 수 없습니다: ${id}`);
  return node;
}

/**
 * 새 배포 뒤 옛 탭을 구한다. 전장 그림(pixi)은 첫 전투를 열 때 조각 파일(`browserAll-*.js` 등)을 뒤늦게 불러오는데, 그
 * 이름이 배포마다 바뀐다. 배포 전에 열어 둔 탭은 사라진 옛 조각을 찾다 실패해 검은 화면에서 멈췄다(플레이한 사람:
 * "갈림길 선택, 군수품 선택 후 이렇게 검은 화면에서 멈춰서 전장진입이 안되네" — 다시 시도하니 됐다). 그러면 새 버전으로
 * 한 번 다시 불러온다. 출정은 계정에 적히지 않으므로 출정 화면으로 돌아가고, 군수품은 판을 정산할 때 주므로 잃지 않는다.
 * 같은 이유로 되풀이하지 않게 30초 안에는 다시 불러오지 않는다.
 */
const RELOAD_KEY = 'ildangbaek.reloadAt';
function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // 저장이 막힌 창이면 되풀이를 막을 길이 없다 — 다시 불러오지 않고 알리기만 한다.
    return false;
  }
  location.reload();
  return true;
}
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForNewVersion()) event.preventDefault();
});

const screenEl = need('screen');
// 메뉴 화면의 밝기. 라이트가 기본이다(ui/theme.ts). 전장(#battle)은 늘 어두운 판이다.
applyTheme(screenEl, savedTheme());
watchSystemTheme(screenEl);
// 소리는 꺼진 채로 시작한다(`audio/settings.ts`). 켠 사람에게만 첫 누름에서 열린다.
initSound();
const battleEl = need('battle');
const stageEl = need('stage');
const overlayEl = need('overlay');

const playtest = currentPlaytest();
/** 자동 출정은 한 번만 쓴다. 이유는 openSortie에 적었다. */
let autoPending = playtest.auto;

/**
 * 계정. 저장이 없으면 나라부터 고른다(`openCulturePick`, Todo9) — 고르기 전까지 쓰는 자리 채움은 고구려다(화면에 나가지
 * 않는다). 시험 모드의 `?culture=`는 그 나라의 새 계정으로 본다(저장하지 않는다).
 */
const saved = loadArmy();
let army: Army =
  playtest.culture !== null ? createStartingArmy(playtest.culture) : (saved ?? createStartingArmy(Culture.Goguryeo));
/** 나라를 아직 고르지 않았다(저장이 없다). 시험 모드는 고르지 않는다 — 계정을 만들지 않으므로 물을 까닭이 없다. */
const needsCulture = saved === null && !isPlaytest(playtest);
let run: RunState | null = null;
/** 지난 판의 결과. 갈림길에서 물러서면 이 결과 화면을 다시 연다(`backFromCrossroads`). */
let lastOutcome: BattleOutcome | null = null;
/**
 * 출정을 시작할 때의 장수·동료 모습. 돌아와 편성 화면을 열 때 견줘, 오른 것이 있으면 축하 장면을 띄운다
 * (`showFanfares`). 새로고침으로 잃으면 그 출정의 축하는 건너뛴다 — 계정에 적어 둘 만한 것은 아니다.
 */
let growthBase: GrowthSnapshot | null = null;
/**
 * 방금 철수해 받은 것. 편성 화면을 열 때 한 번 맨 위에 적고 비운다(`homecomingBar`). 철수를 누르면 곧장 편성 화면이라
 * 월급이 들어왔는지 알 길이 없었다(플레이한 사람). 새로 고치면 잃는다 — 계정에 적어 둘 것은 아니다.
 */
let homecoming: Homecoming | null = null;

/** 출정에서 돌아와 편성 화면을 연 뒤, 오른 관등·레벨을 축하한다. */
function celebrateGrowth(): void {
  const base = growthBase;
  growthBase = null;
  if (!base) return;
  const name = (id: string): string => COMPANIONS.find((c) => c.id === id)?.name ?? id;
  showFanfares(fanfaresFor(base, growthSnapshot(army), name));
}
let session: BattleSession | null = null;
/**
 * 남겨 둔 판의 기록(`meta/replays.ts` — 최근 다섯 판과 전과가 가장 높은 한 판). 출정 화면의 "전투 기록"에서 다시 본다.
 * 시험 모드(`?depth`)는 계정처럼 저장하지 않고 기억에만 둔다(`persistRecords`).
 */
let records: ReplayRecord[] = isPlaytest(playtest) ? [] : loadRecords();
// 계정이 없는데(처음이거나 저장이 깨져 나라부터 다시 고른다) 기록만 남아 있으면 앞 계정의 것이다 — 지운다.
if (needsCulture && records.length > 0) {
  records = [];
  clearRecords();
}
/**
 * 방금 끝난 판의 기록. 결과 화면의 "전투 다시 보기"가 연다. 처음에는 이것 하나만 기억에 두어, 결과 화면을 떠나면 볼 수
 * 없었다 — 이제 `records`에도 남는다. 전사 알림이 부를 이름은 정산 전에 따 둔다(쓰러진 병사는 정산 뒤 계정에 없다).
 */
let lastReplay: ReplayRecord | null = null;
/** 기억의 기록이 저장된 것과 달라졌는가(새 판이 더해졌다). 다음 `persist`가 기록도 저장한다. */
let recordsDirty = false;
/** 열려 있는 다시 보기. `token`은 여는 사이(그림을 읽는 동안) 닫히거나 다시 열렸는지 가른다. */
let replaySession: { renderer: Renderer; view: ReplayView; sound: BattleSound } | null = null;
let replayToken = 0;
// 뒤로가기를 누르면 나갈지 묻는다(`ui/leave-guard.ts`). 묻는 동안 싸움은 선다 — 다시 보기는 잃을 것이 없어 세우지 않는다.
installLeaveGuard({
  // 끝난 판(승패가 떠 있다)은 싸우는 중이 아니다 — 정산 전이라 잃는 것은 출정 중과 같다.
  status: () => (session && !replaySession && !session.loop?.isPaused ? 'battle' : run || session ? 'run' : 'idle'),
  hold: (on) => session?.loop?.hold(on, HOLD_LEAVE),
  host: () => (battleEl.hidden ? screenEl : document.body),
});

/**
 * 계정을 저장한다.
 *
 * **시험 모드에서는 저장하지 않는다.** 깊이를 덮어쓰면 그 값이 경험치·전리품·
 * 전과·영구 사망까지 흘러간다. 깊이 12를 구경하려고 연 판이 최고 전과를
 * 갈아치우고 병사를 영영 죽인다. 잠깐 재 보려고 연 창이 키우던 부대를 망치면
 * 안 된다. 표시줄에도 적어 둔다.
 */
/**
 * 계정을 저장한다. 전투 기록과 같은 한도를 나눠 쓰므로 계정이 먼저다: 계정이 안 들어가면 기록을 오랜 판부터 덜어 낸다
 * (`saveAccountThenRecords`). 기록이 하나도 없으면 계정만 저장한다.
 */
function persist(): void {
  if (isPlaytest(playtest)) return;
  // 기록은 판이 끝날 때만 바뀐다(`recordsDirty`). 물건을 사거나 강화할 때마다 기록 수십 KB를 다시 쓰지 않는다 —
  // 계정이 들어가면 그것으로 끝이다(리뷰 제안). 안 들어가면 기록을 덜어 자리를 낸다.
  if (!recordsDirty && saveArmy(army)) return;
  const had = records.length;
  const kept = saveAccountThenRecords(() => saveArmy(army), records);
  recordsDirty = false;
  // 덜어 냈으면 기억의 목록도 맞춘다. 하나도 못 남겼는데 원래 있었으면(저장을 쓸 수 없는 창) 기억의 것은 둔다 — 이
  // 창에서는 볼 수 있다.
  if (kept.length > 0 || had === 0) records = kept;
}

function showScreen(): void {
  // 화면을 갈아 끼우면 떠 있던 아이템 설명 창이 사라진다. 리스너까지 거둔다(`closeItemTip`).
  closeItemTip();
  battleEl.hidden = true;
  screenEl.hidden = false;
}

function showBattle(): void {
  closeItemTip();
  screenEl.hidden = true;
  battleEl.hidden = false;
}

// ---------------------------------------------------------------------------
// 출정 준비
// ---------------------------------------------------------------------------

function openSortie(): void {
  showScreen();
  clear(screenEl);
  // 다음 출정의 시드를 떠나기 전에 정해 계정에 적는다(`nextRunSeed`). 출정 화면이 출발 깊이의 전장 특성을 보이고,
  // 출정 화면을 다시 열거나 새로 고쳐도 같은 특성이다. 시드는 그 출정의 첫 판을 정산할 때 비운다(`settleRunSeed`)
  // — 첫 판 도중에 새로 고쳐 출정을 버려도 같은 시드로 다시 나선다. 검사는 `nextRunSeed` 하나가 한다 — 여기서
  // `undefined`만 보았더니 저장에 -1 같은 값이 있으면 새 시드를 적지 않아 새로 고칠 때마다 다시 굴렀다(리뷰가 짚었다).
  const before = army.nextSeed;
  if (nextRunSeed(army) !== before) persist();
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  const sortie = new SortieScreen(
    host,
    army,
    (result) => {
      // 급료를 먼저 낸다(`wageFor`, Todo6). 모자라면 출정 화면이 단추를 막으므로 여기 오지 않는다 — 그래도 음수로는 두지 않는다.
      const wage = payWage(army, result.selection.squads.flat());
      if (wage === null) return;
      army.sortieCount++;
      growthBase = growthSnapshot(army);
      // 시험 모드가 시드와 깊이를 덮어쓴다. 그 밖에는 평소 경로 그대로다.
      run = startRun(result.selection, playtest.seed ?? result.seed, false, result.startDepth);
      run.wage = wage;
      if (playtest.depth !== null) run.depth = playtest.depth;
      persist();
      void openBattle();
    },
    persist,
    openArmory,
    openStats,
    resetArmy,
    startRaid,
    openRealm,
    { count: records.length, open: openRecords },
  );
  // 방금 철수했으면 받은 것을 머리줄 밑에 적는다. 닫기 전까지 남고(화면이 쥔다), 다른 화면에 다녀오면 사라진다.
  if (homecoming) {
    sortie.homeNote = homecomingBar(homecoming, () => {
      sortie.homeNote = null;
    });
    homecoming = null;
  }
  sortie.render();
  // 영토 소식은 출정 화면의 아래쪽 "영토" 칸이 늘 보인다(`realmSection`). 우리 나라의 일은 토스트로도 알린다.
  realmToast(host);

  const label = playtestLabel(playtest);
  if (label) host.prepend(el('div', { class: 'playtest-bar' }, label));

  // 병영을 클릭해 채우는 것부터가 준비의 절반이다. 체감을 보려는 판에서는
  // 그 절반을 건너뛴다. 다만 **한 번만**이다.
  //
  // openSortie는 첫 진입 말고도 철수·귀환·장비 화면에서 돌아올 때 불린다.
  // 매번 자동 출정하면 철수하자마자 다음 전투가 시작되어 편성 화면에 영영
  // 설 수 없다. 명령을 바꿔가며 비교하려고 만든 것이 그걸 못 하게 막는다.
  if (autoPending) {
    autoPending = false;
    sortie.deployAllAndStart();
  }
}

/**
 * 장수 혼자 도적떼를 치러 나간다.
 *
 * 부대를 전부 잃으면 돈이 나올 데가 없다. 돈은 전투에서만 나오는데 전투에는
 * 병사가 필요했다. 토벌은 병사 없이 장수만으로 성립하는 유일한 싸움이고,
 * 그래서 바닥에서 일어서는 길이 된다.
 */
function startRaid(band = 0): void {
  // 닫힌 무리(관등이 모자라거나 흩어진 무리)로는 나서지 않는다 — 화면이 단추를 막지만 여기서도 한 번 본다.
  if (!canRaid(army, band)) band = 0;
  // **출정 횟수를 올리지 않는다.** 동료의 휴식 시계가 이 수를 센다
  // (`readyAt <= sortieCount`). 토벌은 져도 잃는 것이 없으므로, 여기서 수를
  // 올리면 "혼자 나선다"를 두 번 눌러 바로 죽는 것만으로 부상당한 동료를
  // 복귀시킬 수 있다. 쉬는 값을 치르지 않고 쉰 것이 된다.
  const empty: Selection = { squads: [[], [], [], []], companions: [null, null, null, null] };
  growthBase = growthSnapshot(army);
  run = startRun(empty, playtest.seed ?? ((Date.now() & 0x7fffffff) ^ 0x7a1d), true, 1, band);
  persist();
  void openBattle();
}

/**
 * 계정을 버리고 처음부터 시작한다.
 *
 * 되돌릴 수 없으므로 한 번 묻는다. 병사 영구 사망이 이 게임의 무게인데, 그
 * 무게를 지우는 단추가 실수로 눌려서는 안 된다.
 */
function resetArmy(): void {
  const lost = army.fallen;
  const merit = army.bestMerit;
  const ok = confirm(
    `모든 것을 버리고 처음부터 시작합니다.\n\n` +
      `최고 전과 ${merit} · 잃은 병사 ${lost}명\n\n` +
      `되돌릴 수 없습니다. 계속할까요?`,
  );
  if (!ok) return;
  clearArmy();
  // 앞 계정의 판을 새 계정의 기록으로 보이지 않는다.
  records = [];
  lastReplay = null;
  clearRecords();
  // 새 계정의 차례 번호는 1부터 다시 센다 — 앞 계정에서 알린 차례와 겹쳐 새 소식을 삼키지 않게(리뷰 제안).
  toastShownTurn = -1;
  run = null;
  growthBase = null;
  // 나라부터 다시 고른다. 고르기 전에 새로 고치면 저장이 없으니 다시 이 화면이다.
  openCulturePick();
}

/** 나라를 고른다(`renderCulturePick`). 고르면 그 나라의 새 계정으로 출정 화면을 연다. */
function openCulturePick(): void {
  showScreen();
  clear(screenEl);
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  renderCulturePick(host, (culture) => {
    army = createStartingArmy(culture);
    run = null;
    growthBase = null;
    persist();
    openSortie();
  });
  screenEl.scrollTop = 0;
}

// ---------------------------------------------------------------------------
// 장비
// ---------------------------------------------------------------------------

function openArmory(who?: string): void {
  showScreen();
  clear(screenEl);
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  new ArmoryScreen(host, army, openSortie, persist, who).render();
}

/** 우리 나라의 일을 토스트로 알린 차례(화면 세션에서 한 번). */
let toastShownTurn = -1;

/**
 * 이번 차례에 난 **우리 나라의 일**(막아 냄·빼앗김·특산)을 토스트로 알린다(Todo11). 소식은 화면 아래쪽의 영토 칸에
 * 있어(`realmSection`) 위를 보던 사람은 놓친다. 위쪽(머리줄 바로 아래)에 띄운다 — 아래에는 출정 단추 줄이 있다.
 */
function realmToast(host: HTMLElement): void {
  const realm = army.realm;
  // 지도를 이미 열어 본 차례면 알리지 않는다 — 화면 세션 변수만 보면 새로 고칠 때마다 본 소식이 다시 떴다(QA).
  if (!realm || toastShownTurn === realm.turn || (realm.newsSeen ?? 0) >= realm.turn) return;
  const fresh = (realm.log ?? []).filter((e) => e.turn === realm.turn && isOurNews(e));
  if (fresh.length === 0) return;
  toastShownTurn = realm.turn;
  const n = el('div', { class: 'buy-toast news-toast', role: 'status' }, ...fresh.map((e) => newsLine(e)));
  host.append(n);
  // 머리줄(출정 준비 · 능력치 · 장비) 바로 아래에 띄운다. 화면 맨 위에 두었더니 6초 동안 머리줄의 단추를 덮었다.
  const nav = host.querySelector('.sortie-nav');
  if (nav) n.style.top = `${Math.round(nav.getBoundingClientRect().bottom + 8)}px`;
  setTimeout(() => n.remove(), NEWS_TOAST_MS);
}
/** 소식 토스트가 떠 있는 시간. 한 줄 알림(1.8초)보다 길다 — 읽을 글이 문장이다. index.html의 `.news-toast`와 같아야 한다. */
const NEWS_TOAST_MS = 6000;

/** 영토전 지도(Todo9). 세계를 처음 열면 계정에 적는다. */
function openRealm(): void {
  showScreen();
  clear(screenEl);
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  const had = army.realm !== undefined;
  new RealmScreen(host, army, openSortie, startTerritory, persist).render();
  // 지도를 열었으니 영토 단추의 빨간 점을 끈다(`hasUnseenNews`).
  const seen = markNewsSeen(army.realm!);
  if (!had || seen) persist();
  screenEl.scrollTop = 0;
}

/**
 * 영토를 치러 나선다(Todo9 4단계). 편성은 출정 화면에서 짜 둔 것(`restorePlan`, 없으면 자동 편성)이다. 급료를 내고
 * 출정 횟수를 센다 — 동료의 요양 시계가 출정 횟수로 돌므로, 영토 공격도 한 번의 나섬이다.
 */
function startTerritory(id: string): void {
  const plan = restorePlan(army) ?? autoPlan(army);
  const uids = plan.squads.flat();
  if (uids.length === 0) return;
  const wage = payWage(army, uids);
  if (wage === null) return;
  // 영토 판의 시드: 출정 시드(`nextRunSeed`)는 출정 화면의 예보에 매여 있어 쓰지 않는다. 시험 모드면 고정 시드.
  const seed = playtest.seed ?? ((Date.now() & 0x7fffffff) ^ (realmOf(army).turn * 7919));
  const next = startTerritoryRun(army, { squads: plan.squads, companions: plan.companions, escort: plan.escort ?? null }, seed, id);
  if (!next) {
    army.gold += wage;
    return;
  }
  army.sortieCount++;
  growthBase = growthSnapshot(army);
  run = next;
  run.wage = wage;
  persist();
  void openBattle();
}

function openStats(): void {
  showScreen();
  clear(screenEl);
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  new StatsScreen(host, army, openSortie, persist).render();
}

// ---------------------------------------------------------------------------
// 전투
// ---------------------------------------------------------------------------

/** 그 명부에 나간 병사의 이름(uid → 이름). 전사 알림이 부른다. */
function rosterNames(config: ScenarioConfig): Map<number, string> {
  const names = new Map<number, string>();
  for (const e of config.roster) {
    for (const uid of e.uids ?? []) {
      const s = soldierById(army, uid);
      if (s) names.set(uid, s.name);
    }
  }
  return names;
}

async function openBattle(): Promise<void> {
  if (!run) return;
  // 갈림길(`needsRoute`): 새 구간의 첫 판이면 갈 길부터 고른다. 길이 땅을 정하므로 협곡 분대·군수품 고르기보다 앞이다.
  if (needsRoute(run)) {
    showScreen();
    clear(screenEl);
    const host = el('div', { class: 'screen-scroll' });
    screenEl.append(host);
    renderCrossroads(
      host,
      army,
      run,
      (route) => {
        if (!run) return;
        chooseRoute(run, route);
        void openBattle();
      },
      () => {
        if (!run) return;
        const back = backFromCrossroads(army, run);
        if (back === 'start') {
          run = null;
          growthBase = null;
          persist();
          openSortie();
        } else if (back === 'result' && lastOutcome) {
          persist();
          // 맞이 장면(동료 합류)은 이미 봤다 — 결과로 곧장.
          openResult(lastOutcome, true);
        }
      },
    );
    screenEl.scrollTop = 0;
    return;
  }
  // 전투 준비(`needsPrep`): 좁은 협곡이면 데려갈 분대를, 군수품이 있으면 이번 판에 쓸 것을 먼저 고른다. 진군해서
  // 왔든 출정 첫 판이든 여기 한 곳에서 묻는다.
  if (needsPrep(army, run)) {
    showScreen();
    clear(screenEl);
    const host = el('div', { class: 'screen-scroll' });
    screenEl.append(host);
    renderBattlePrep(host, army, run, (c) => {
      if (!run) return;
      if (c.squads) pickSquads(run, c.squads);
      if (c.supplies) chooseSupplies(army, run, c.supplies);
      if (c.deploy) chooseDeploy(army, run, c.deploy);
      persist();
      void openBattle();
    });
    screenEl.scrollTop = 0;
    return;
  }
  showBattle();
  clear(overlayEl);

  const config = buildConfig(army, run);
  const world = createBattle(config);
  const deployed = config.roster.reduce((n, e) => n + e.count, 0);

  let renderer: Renderer;
  try {
    renderer = await Renderer.create(world, stageEl);
  } catch (err) {
    // 전장 그림을 열지 못했다(대개 새 배포 뒤 옛 탭 — 위 `reloadForNewVersion`). 검은 화면에 멈춰 두지 않는다.
    console.error(err);
    if (reloadForNewVersion()) return;
    overlayEl.append(
      el(
        'div',
        { class: 'hud battle-fail', role: 'alert' },
        el('b', {}, '전장을 열지 못했다'),
        el('span', {}, '새 버전이 나왔을 수 있다. 다시 불러오면 출정 화면으로 돌아간다.'),
        button('다시 불러오기', () => location.reload(), 'primary'),
      ),
    );
    return;
  }
  // 비가 오는 판이면 빗줄기, 밤이면 어둠과 보이는 거리(그림뿐이다). 횃불(군수품)이면 더 멀리 보인다.
  renderer.setRain(battleTraits(config).includes(Trait.Rain));
  renderer.setFog(battleTraits(config).includes(Trait.Fog));
  renderer.setNight(
    battleTraits(config).includes(Trait.Night) ? (battleSupplies(config).includes(Supply.Torch) ? NIGHT_SIGHT_TORCH : NIGHT_SIGHT) : 0,
  );
  // 전사 알림이 이름을 부른다. 이 판에 나간 병사만 싣는다.
  const names = rosterNames(config);
  const hud = new Hud(overlayEl, {
    deployed,
    raid: run?.raid === true,
    bounty: RAID_BANDS[raidBand(config.raidBand)].bounty,
    names,
    traits: battleTraits(config),
    gate: config.raid ? null : gateStoryName(config.depth),
  });
  const input = new InputState();
  input.attach(stageEl, (x, y) => renderer.screenToWorld(x, y));
  // 첫 틱 전에 화면 방향을 맞춘다. 첫 render 뒤에야 맞추면 가로로 시작한 판의 첫 틱 입력이 옛 방향으로 실렸다.
  renderer.orient();
  input.setRotated(renderer.rotated);
  const controls = new BattleControls(overlayEl, input, world);
  controls.dockRoar(hud.roarDock);
  // 처음 고른 부대는 전 부대다(시뮬레이션의 기본). 예전에는 내 부대를 먼저 골라 두었는데, 플레이한
  // 사람이 "기본값으로 모든 부대 명령상태로" 달라고 했다. 한 부대에만 내릴 때는 부대선택 목록에서 고른다.

  const current: BattleSession = {
    renderer,
    loop: null as never,
    hud,
    input,
    controls,
    world,
    config,
    ended: false,
  };

  // 적장이 쓰러지는 틱에 한 번 알린다. 전군의 사기가 꺾이는 순간이라(`CHIEF_FALL_PCT`) ▼만으로는 까닭이 안 보인다.
  // 적장 결전은 적장이 셋이라 하나씩 쓰러질 때마다 남은 수를 알린다 — 마지막이 쓰러져야 적이 무너진다.
  let chiefsUp = world.chiefsAlive();
  // 총대장이 살아 있었는가 — 부장이 쓰러진 것과 가른다(적의 부대, `FOE_SQUADS`).
  let chiefUp = world.chiefAlive();
  // 부대마다 대장이 살아 있었는가 — 누가 쓰러졌는지 이름으로 알린다(`meta/foe-leaders.ts`).
  const cmdUp = Array.from(world.foeCmd, (c) => c >= 0 && world.alive[c] === 1);
  // 판을 열 때 한 번: 누가 이끄는 군대인가. 토벌은 두령이라 알리지 않는다.
  if (run?.raid !== true && world.chiefIdx >= 0) {
    const head = foeLeaderOf(world, world.chiefIdx);
    // 적장 결전은 0·1·2번 부대의 대장이 적장이고 나머지가 부장이다(`foeHeadSquad`).
    const heads: string[] = [];
    const subs: string[] = [];
    for (let sq = 1; sq < world.foeCmd.length; sq++) {
      const who = world.foeCmd[sq] >= 0 ? foeLeaderOf(world, world.foeCmd[sq]) : null;
      if (who) (foeHeadSquad(sq, world.warlords) ? heads : subs).push(who.name);
    }
    if (head) {
      const more = subs.length > 3 ? ` 외 ${subs.length - 3}` : '';
      const rest =
        (heads.length > 0 ? `<br>적장 ${heads.join('·')}` : '') + (subs.length > 0 ? `<br>적 부장 ${subs.slice(0, 3).join('·')}${more}` : '');
      // 네 초쯤 띄운다 — 판을 연 직후에는 눈이 조작 쪽에 있어 2.6초로는 놓치기 쉬웠다(QA).
      hud.say(`<b>적장 ${head.name}</b> <span style="white-space:nowrap">— ${head.who}</span>${rest}`, 240);
    }
  }
  let fallAnnounced = false;
  /** 토벌에서 물러난다는 알림을 냈는가(한 번만). */
  let fleeSaid = false;
  // 동료 고유 기술(Todo10)이 걸리는 틱에 한 번 알린다 — 단추 없이 스스로 쓰는 기술이라 알리지 않으면 지나간다.
  const mateSeen = Array.from(world.mateCount);
  const autoSeen = Array.from(world.sqAutoOut);
  // 다시 보기의 시간 줄에 찍을 주요 장면을 싸우는 동안 적는다(`game/replay-moments.ts`).
  const tracker = new MomentTracker(world, run?.raid === true);
  current.moments = tracker;
  // 판의 소리: 틱 뒤마다 상태를 견줘 기술·대장이 쓰러짐을 낸다. 판의 결과에는 손대지 않는다(읽기만 한다).
  const sound = new BattleSound(world, 1, battleTraits(config).includes(Trait.Night));
  current.sound = sound;
  const hear = (x: number, y: number, out: { pan: number; dist: number }): void => renderer.hear(x, y, out);
  const loop = new GameLoop(world, renderer, input, {
    onTick: (w) => {
      if (current.ended) return;
      tracker.observe(w);
      sound.tick(w, 1, hear);
      // 같은 틱에 여럿이 걸리면 한 알림에 줄마다 적는다 — 하나씩 띄웠더니 마지막 것이 앞의 것을 덮었다(리뷰·QA).
      // 부대 이름은 줄표와 한 덩어리로(폰에서 "제 / 4부대"로 끊겼고, 그 뒤에는 줄표만 줄 끝에 남았다 — QA).
      const fired: string[] = [];
      for (let sq = 0; sq < mateSeen.length; sq++) {
        if (w.mateCount[sq] === mateSeen[sq]) continue;
        mateSeen[sq] = w.mateCount[sq];
        const skill = mateSkillOf(w, sq);
        const who = COMPANIONS.find((c) => c.id === skill?.id);
        // 둘레 기술(지장)은 분대가 아니라 둘레의 아군에게 걸린다.
        const whom = w.mateRadius[sq] > 0 ? '둘레의 아군' : squadName(sq);
        if (skill && who) fired.push(`<b>${who.name}의 ${skill.name}</b> <span style="white-space:nowrap">— ${whom}</span>`);
      }
      if (fired.length > 0) hud.say(fired.join('<br>'), 120, 'good');
      // 대형의 기병 분대가 스스로 공격에 나섰다(`World.sqAuto`) — 누르지 않은 명령이 바뀌므로 알린다. 처음에는 알리지
      // 않았다: 판을 열고 0.3초 만에 명령 표시만 "공격"으로 바뀌어 왜 그런지 알 수 없었다(QA).
      // 한 틱에 둘이 나서면 이름을 모아 한 번에 띄운다 — 따로 띄웠더니 뒤의 것이 앞의 것을 덮었다(QA: 제2·제4부대가
      // 함께 나선 판에 제4부대만 떴다).
      const out: string[] = [];
      for (let sq = 0; sq < autoSeen.length; sq++) {
        if (w.sqAutoOut[sq] === autoSeen[sq]) continue;
        autoSeen[sq] = w.sqAutoOut[sq];
        out.push(squadName(sq));
      }
      if (out.length > 0) {
        hud.say(`<b>${out.join('·')}의 기병이 돌아 들어간다</b> <span style="white-space:nowrap">— 붙들려면 대형</span>`, 150);
      }
      const chiefsNow = w.chiefsAlive();
      if (chiefsNow < chiefsUp) {
        chiefsUp = chiefsNow;
        if (run?.raid !== true) {
          // 방금 쓰러진 대장들의 이름(부대 번호 순). 이름을 모르면(없는 칸) 예전 문구로 적는다.
          const fell = fallenLeaders(w, cmdUp);
          const title = fallTitle(fell.heads, fell.subs);
          if (!w.warlords) {
            // 총대장이면 전군이, 부장이면 그 부대만 흔들린다.
            const subsLeft = chiefsNow - (w.chiefAlive() ? 1 : 0);
            if (chiefUp && !w.chiefAlive()) hud.say(`<b>${title || '적장이 쓰러졌다'}</b> — 적의 사기가 꺾였다`, 150, 'good');
            else hud.say(`<b>${title || '적의 부장이 쓰러졌다'}</b> — 그 부대가 흔들린다${chiefsNow > 1 ? ` · 남은 적 부장 ${subsLeft}` : ''}`, 150, 'good');
            chiefUp = w.chiefAlive();
          }
          // 적장 결전: 적장 셋을 모두 베어야 무너진다 — 부장은 제 부대만 흔든다(118부터 결전에도 부장이 선다).
          // "모두 베었다"는 마지막 적장이 쓰러진 그 틱에만 — 그 뒤에 쓰러지는 부장까지 같은 글로 되풀이하지 않는다(리뷰).
          else if (w.headsAlive() === 0 && (fell.heads.length > 0 || title === '')) hud.say('<b>적장을 모두 베었다</b> — 적이 무너진다', 180, 'good');
          else if (fell.heads.length > 0 || title === '') hud.say(`<b>${title || '적장 하나가 쓰러졌다'}</b> — 남은 적장 ${w.headsAlive()}`, 150, 'good');
          else hud.say(`<b>${title}</b> — 그 부대가 흔들린다${w.headsAlive() > 0 ? ` · 남은 적장 ${w.headsAlive()}` : ''}`, 150, 'good');
        }
      }
      // 전장 위에 남은 적만 센다. 달아나 빠져나간 적은 싸움에서 빠진 것이다.
      const enemyLeft = w.aliveCount(Team.Enemy);
      const ourTroops = w.soldiersOnField(Team.Player);
      const raid = run?.raid === true;
      // 토벌에서 후퇴를 처음 누르면 무슨 일이 되는지 알린다 — 출정의 후퇴(병사가 빠져나간다)와 뜻이 다르다.
      if (raid && !fleeSaid && w.leaderAlive() && w.sqOrder[w.squad[w.leaderIdx]] === Order.Retreat) {
        fleeSaid = true;
        // 가로 화면은 전장을 돌려 그린다 — 전장의 아래 끝이 화면의 왼쪽이다. "아래 끝"이라고만 적으면 PC에서 어디인지
        // 헷갈린다(QA).
        hud.say(`<b>물러난다</b> — ${renderer.rotated ? '왼쪽' : '아래'} 끝에 닿으면 토벌을 접는다. 잡은 만큼은 챙긴다(현상금은 없다)`, 150, 'bad');
      }
      // 장수가 쓰러지면 바로 끝내지 않는다. 남은 병력이 아래 가장자리로 후퇴하고(`World.leaderFall`), 빠져나간 병사는
      // 살아 돌아온다. 전장에 아군이 남지 않거나 후퇴 시간이 지나면 끝난다(`World.fallOver`).
      if (!w.leaderAlive()) {
        if (!fallAnnounced) {
          fallAnnounced = true;
          // 명령할 사람이 없다(시뮬레이션도 명령을 받지 않는다). 누를 수 있어 보이면 왜 안 먹는지 헷갈린다.
          controls.suspend();
          if (!raid) hud.say('<b>장수가 쓰러졌다</b> — 남은 병력이 퇴각한다. 아래로 빠져나간 병사는 살아 돌아온다', 240, 'bad');
        }
        if (w.fallOver()) finish(current, '장수가 쓰러졌다', false);
      }
      else if (raid && !w.chiefAlive()) finish(current, '두령을 베었다', true);
      // 토벌에서 물러난다: 후퇴를 누르면 장수가 아래 끝으로 달리고, 닿으면 판을 접는다(`World.leaderFled`). 잡은 만큼은
      // 받는다(`applyBattle`) — 하나도 못 잡았으면 "챙겼다"고 적지 않는다(QA).
      else if (raid && w.leaderFled()) finish(current, w.stats.killScore[Team.Player] > 0 ? '물러났다 — 잡은 만큼은 챙겼다' : '물러났다', false);
      else if (enemyLeft === 0) finish(current, '전장을 쓸었다', true);
      // 토벌은 장수 혼자 나가므로 병력이 0인 것이 정상이다.
      else if (!raid && ourTroops === 0) {
        // 전장에 병사가 없다는 것은 같아도, 빠져나간 병사가 있으면 물러난 것이다.
        finish(current, w.stats.exited[Team.Player] > 0 ? '병력이 전장을 빠져나갔다' : '병력이 전부 쓰러졌다', false);
      }
    },
    onFrame: (fps, simMs) => {
      // 끄는 동안 카메라는 손가락 앞을 본다. 다음 프레임부터 반영된다.
      renderer.lookAhead = input.drag;
      renderer.pendingPath = input.pendingPath;
      hud.update(world, fps, simMs);
      const size = renderer.screenSize();
      hud.pointToEnemies(world, { w: size.w, h: size.h, toScreen: (x, y, out) => renderer.worldToScreen(x, y, out) });
      // 가로 화면이면 조이스틱·키 방향을 월드로 돌려 싣는다(`InputState.rotated`).
      input.setRotated(renderer.rotated);
      controls.update();
      if (input.requestVerify) {
        input.requestVerify = false;
        verify(current, config);
      }
    },
  });
  current.loop = loop;
  // 부대 고르기: 게임을 세우고, 카메라를 지휘관에게, 부대원을 깜빡인다(`BattleControls.beginPick`).
  controls.bindPick(
    {
      hold: (on) => {
        loop.hold(on);
        renderer.frozen = on;
      },
      show: (mask) => {
        renderer.pickMask = mask;
      },
    },
    stageEl,
  );
  // 개발 서버에서만: 브라우저 시험이 지휘관 자리를 재도록 판을 내어 준다. 배포 빌드에는 없다.
  if (import.meta.env.DEV) (window as unknown as { __battle?: unknown }).__battle = { world, renderer };
  session = current;
  loop.start();
}

function finish(s: BattleSession, title: string, won: boolean): void {
  s.ended = true;
  s.sound?.end(won);
  s.loop.pause();
  // 조작만 멈춘다. 통째로 떼면 종료 패널에서 결정론 검증 키까지 죽는다.
  s.input.suspend();
  s.controls.suspend();
  const panel = el(
    'div',
    { class: 'hud hud-end' },
    el('b', {}, title),
    button('결과 보기', () => closeBattle(), 'primary'),
  );
  overlayEl.append(panel);
}

/** 브라우저 안에서 서버 검증과 똑같은 일을 해본다. */
function verify(s: BattleSession, config: ReturnType<typeof buildConfig>): void {
  const ticks = s.world.tick;
  if (ticks < 2) return;
  const live = hashToHex(s.world.checksum());
  const t0 = performance.now();
  const { world: replayed } = runReplay({
    version: REPLAY_VERSION,
    config,
    ticks,
    commands: s.loop.snapshotLog(),
  });
  const ms = performance.now() - t0;
  const again = hashToHex(replayed.checksum());
  s.hud.say(
    [
      `<b>${again === live ? '재현 일치' : '재현 불일치'}</b>`,
      `${ticks}틱 · 커맨드 ${s.loop.snapshotLog().length / 4}개 · 재생 ${ms.toFixed(0)}ms`,
      `현재 ${live} / 재생 ${again}`,
    ].join('<br>'),
    360,
    again === live ? 'good' : 'bad',
  );
}

function closeBattle(): void {
  if (!session || !run) return;
  // 다시 보기의 기록. 정산(`applyBattle`)보다 먼저 딴다 — 명부는 판을 연 그대로여야 하고(정산이 건드리지 않게 베낀다),
  // 쓰러진 병사의 이름은 정산 뒤 계정에서 사라진다. 서버에 내는 기록과 같은 꼴이다(`verify`).
  const replay: Replay = {
    version: REPLAY_VERSION,
    config: structuredClone(session.config),
    ticks: session.world.tick,
    commands: session.loop.snapshotLog(),
  };
  const names = [...rosterNames(session.config)];
  const moments = session.moments ? [...session.moments.moments] : [];
  const land = run.territory ? TERRITORY_BY_ID.get(run.territory.id) : undefined;
  const outcome = applyBattle(army, run, session.world, session.config);
  // 같은 깊이를 여러 번 싸우면 제목이 같아 목록에서 가를 수 없었다(QA가 짚었다) — 그 출정의 몇 번째 판인지 적는다
  // (결과 화면의 머리와 같은 글). 판 수는 정산이 센다.
  const title = land ? `영토전 · ${land.name}` : run.raid ? '단신 토벌' : `깊이 ${run.depth} · ${run.battles}번째 전투`;
  // 물러난 토벌은 진 것이 아니다 — 기록 카드가 "패배"라고 적지 않게 따로 표시한다(QA).
  const fled = run.raid === true && !outcome.won && outcome.leaderAlive;
  lastReplay = { at: Date.now(), replay, names, raid: run.raid === true, title, won: outcome.won, ...(fled ? { fled } : {}), merit: outcome.battleMerit, moments };
  // 기록은 아래의 `persist`가 계정 뒤에 저장한다 — 계정이 먼저다.
  records = addRecord(records, lastReplay);
  recordsDirty = true;
  persist();

  session.input.detach();
  session.controls.detach();
  session.renderer.destroy();
  // 결과 화면으로 넘어가며 이김·짐의 꼬리가 이어지는 것은 둔다 — 여기서 끊으면 판 끝의 소리가 누르는 순간 잘린다.
  clear(stageEl);
  clear(overlayEl);
  session = null;

  openResult(outcome);
}

// ---------------------------------------------------------------------------
// 다시 보기
// ---------------------------------------------------------------------------

/**
 * 방금 끝난 판을 다시 본다(`game/replay-player.ts`). 계정과 출정(`army`·`run`)은 건드리지 않는다 — 기록에서 판을 새로
 * 세워 커맨드만 넣는다. `at`은 되감을 때: 판을 새로 열어 그 틱까지 그리지 않고 돌린 뒤, 보던 상태(속도·카메라)를 잇는다.
 * 시뮬레이션은 앞으로만 가므로 뒤로 가려면 이 길뿐이다.
 */
async function openReplay(rec: ReplayRecord, back: 'result' | 'records', at?: { tick: number; state: ReplayViewState }): Promise<void> {
  const token = ++replayToken;
  dropReplay();
  showBattle();
  clear(stageEl);
  clear(overlayEl);

  const { replay } = rec;
  let player: ReplayPlayer;
  let renderer: Renderer;
  try {
    player = new ReplayPlayer(replay);
    if (at) player.seek(at.tick);
    renderer = await Renderer.create(player.world, stageEl);
  } catch (err) {
    // 기록을 못 열어도 결과로 돌아간다. 검은 전장에 세워 두지 않는다.
    console.error(err);
    if (token === replayToken) leaveReplay(back);
    return;
  }
  // 그림을 읽는 사이에 닫았거나 다시 열었다 — 이 판은 버린다.
  if (token !== replayToken) {
    renderer.destroy();
    return;
  }
  // 비는 그림뿐이라 그대로 긋는다. 밤의 어둠과 안개는 걷는다 — 싸울 때는 장수 둘레만 보이는 것이 규칙이지만, 다시 볼
  // 때는 다른 부대와 적장까지 따라가 보는 것이 목적이다.
  renderer.setRain(battleTraits(replay.config).includes(Trait.Rain));
  renderer.orient();
  const hud = new Hud(overlayEl, {
    deployed: replay.config.roster.reduce((n, e) => n + e.count, 0),
    raid: rec.raid,
    bounty: RAID_BANDS[raidBand(replay.config.raidBand)].bounty,
    names: new Map(rec.names),
    traits: battleTraits(replay.config),
    gate: replay.config.raid ? null : gateStoryName(replay.config.depth),
  });
  overlayEl.classList.add('replay');

  const world = player.world;
  let view: ReplayView | null = null;
  // 다시 보기의 소리. 되감았으면 건너뛴 뒤의 상태가 기준이라 그 사이의 일은 나지 않는다. 배속은 틱마다 함께 넘긴다 —
  // 4배부터는 전장 소리를 내지 않는다(`AudioDirector`).
  const sound = new BattleSound(world, at?.state.speed ?? 1);
  // 다시 볼 때는 어둠을 걷으므로(위) 밤의 판도 자리를 싣는다.
  const hear = (x: number, y: number, out: { pan: number; dist: number }): void => renderer.hear(x, y, out);
  const loop = new ReplayLoop(player, renderer, {
    onStep: () => sound.tick(world, loop.speed, hear),
    onFrame: () => {
      hud.update(world, 0, 0);
      view?.update();
    },
    onEnd: () => view?.showEnd(),
  });
  view = new ReplayView({
    root: overlayEl,
    stage: stageEl,
    player,
    loop,
    renderer,
    init: at?.state ?? { speed: 1, playing: true, focus: { kind: 'squad', squad: 0 }, zoom: null, cam: null },
    moments: rec.moments,
    // 싸울 때처럼 무슨 일인지 알린다(적장이 쓰러졌다 등).
    // 글은 기록에서 왔다(저장을 믿지 않는다) — 태그가 되지 않게 글로만 넣는다.
    onMoment: (recent) => hud.say(recent.map((m) => `<b>${plain(m.label)}</b>`).join('<br>'), 150, momentTone(recent.map((m) => m.kind))),
    onRewind: (tick, state) => void openReplay(rec, back, { tick, state }),
    onClose: () => leaveReplay(back),
  });
  replaySession = { renderer, view, sound };
  if (import.meta.env.DEV) (window as unknown as { __battle?: unknown }).__battle = { world, renderer, player };
  loop.start();
}

/** 열려 있는 다시 보기의 그림과 조작을 걷는다. */
function dropReplay(): void {
  if (!replaySession) return;
  replaySession.view.detach();
  replaySession.renderer.destroy();
  replaySession.sound.close();
  replaySession = null;
  overlayEl.classList.remove('replay');
}

/** 다시 보기를 닫고 온 곳으로 돌아간다: 그 판의 결과, 또는 전투 기록 목록. */
function leaveReplay(back: 'result' | 'records'): void {
  replayToken++;
  dropReplay();
  clear(stageEl);
  clear(overlayEl);
  if (back === 'records') openRecords();
  // 맞이 장면(동료 합류)은 이미 봤다 — 결과로 곧장.
  else if (lastOutcome && run) openResult(lastOutcome, true);
  else openSortie();
}

/** HTML로 넣는 자리에 글을 글로만: 태그·속성이 될 글자를 바꾼다. */
function plain(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

/** 전투 기록 목록(출정 화면에서). */
function openRecords(): void {
  showScreen();
  clear(screenEl);
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);
  renderRecords(host, records, {
    onPlay: (rec) => void openReplay(rec, 'records'),
    onBack: openSortie,
  });
  screenEl.scrollTop = 0;
}

// ---------------------------------------------------------------------------
// 결과
// ---------------------------------------------------------------------------

function openResult(outcome: BattleOutcome, sceneShown = false): void {
  if (!run) return;
  lastOutcome = outcome;
  showScreen();
  clear(screenEl);

  // 동료가 합류했으면 결과보다 먼저 그 사람을 맞이한다. 한 번만 — 맞이한 뒤에는 결과로.
  const joined = companionToGreet(outcome.joined, sceneShown);
  if (joined) {
    renderJoinedScene(screenEl, joined, () => openResult(outcome, true));
    screenEl.scrollTop = 0;
    return;
  }
  const host = el('div', { class: 'screen-scroll' });
  screenEl.append(host);

  renderResult(host, army, run, outcome, {
    // 방금 끝난 판의 기록이 있을 때만(창을 새로 열어 결과부터 보는 일은 없지만, 없으면 단추도 없다).
    onReplay: lastReplay ? () => lastReplay && void openReplay(lastReplay, 'result') : undefined,
    onAdvance: () => {
      if (!run) return;
      advance(run);
      void openBattle();
    },
    onRetreat: () => {
      if (!run) return;
      // 영토 공격(Todo9)이었으면 지도로 돌아간다 — 빼앗은 땅이 바로 보인다.
      const toMap = run.territory !== undefined;
      // 받은 것을 떠 둔다 — 편성 화면 맨 위에 적는다(`homecomingBar`). 영토 공격은 지도로 돌아가므로 적지 않는다.
      const receipt = toMap ? null : retreatReceipt(army, run);
      const goldBefore = army.gold;
      retreat(army, run);
      // 철수하면 세계가 한 차례 돈다 — 영토에서 들어온 돈도 같이 적는다(군자금 차이에서 노획·월급을 뺀 나머지).
      homecoming = receipt ? { ...receipt, other: Math.max(0, army.gold - goldBefore - receipt.loot - receipt.salary) } : null;
      run = null;
      persist();
      if (toMap) openRealm();
      else openSortie();
      celebrateGrowth();
    },
    onReturn: () => {
      const toMap = run?.territory !== undefined;
      run = null;
      persist();
      if (toMap) openRealm();
      else openSortie();
      celebrateGrowth();
    },
  });
  // 결과는 늘 맨 위(제목·전과)부터 보인다. 스크롤하는 것은 화면(#screen)이라 내용을 갈아 끼워도 위치가 남는다 —
  // 지난 판 결과를 아래(전공 순위)까지 내려 두면 다음 판 결과가 그 높이에서 열렸다(플레이한 사람이 짚었다).
  // 같은 작업 안에서 비우고 다시 채우면 브라우저가 그 사이에 위치를 0으로 당길 틈이 없다. 그래서 채운 뒤에 올린다.
  screenEl.scrollTop = 0;
}

if (needsCulture) openCulturePick();
else openSortie();
